import { ArrowRight, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { DeltaCell } from "@/components/cinch/DeltaCell";
import { EntityDrawer } from "@/components/cinch/EntityDrawer";
import { ReviewVerdict } from "@/components/cinch/ReviewVerdict";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  monthLabel,
  useReplacementHistory,
  useReviewReplacement,
  REPLACEMENT_FLAG_LABELS,
  SUPPRESSION_LABELS,
  type ReplacementHistory,
  type ReplacementRow,
} from "../api";
import { hubspotDealUrl, pandadocUrl } from "../../contracts/links";
import { fmtCur, fmtCur2, fmtDate } from "../../renewals/format";
import { ReplacementLineTable } from "./ReplacementLineTable";
import { ReplacementVerifyPanel } from "./ReplacementVerifyPanel";

const FAILING = new Set([
  "replaced_still_open",
  "amendment_still_open",
  "handoff_gap",
  "handoff_overlap",
]);

/** Review view for one replacement: what it's worth, whether the contract it
 *  replaced was actually closed, and — expanded — the twelve months of billing
 *  behind the comparison. */
export function ReplacementDrawer({
  period,
  row,
  canAct,
  expanded,
  presetFlag,
  onExpandedChange,
  onClose,
}: {
  period: string;
  row: ReplacementRow | null;
  canAct: boolean;
  expanded: boolean;
  presetFlag: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onClose: () => void;
}) {
  const review = useReviewReplacement(period);
  const history = useReplacementHistory(period, expanded ? (row?.contract_id ?? null) : null);

  return (
    <EntityDrawer
      open={row != null}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      title={row ? (row.account_name ?? row.company_name ?? "Replacement") : ""}
      subtitle={
        row
          ? `${row.contract_type_name ?? "Replacement"} · signed ${fmtDate(row.sign_date)} · ${monthLabel(period)} close`
          : undefined
      }
      onClose={onClose}
    >
      {row && (
        <div className="flex flex-col gap-5 p-4">
          {!row.in_period && row.is_open && (
            <div className="rounded-md border border-warning/40 bg-warning-light/50 px-3 py-2 text-xs text-warning">
              Signed {fmtDate(row.sign_date)}, still awaiting verification. It
              doesn't count toward this month's bookings — it's on the list until
              someone settles what it replaced and what it netted.
            </div>
          )}

          {/* The headline: what this replacement actually added */}
          <div className="rounded-md border px-4 py-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Net change
              </span>
              {row.valued ? (
                <DeltaCell value={row.net_change_arr} chip emptyLabel="no change" />
              ) : (
                <span className="text-xs font-medium text-muted-foreground">
                  unvalued
                </span>
              )}
            </div>
            {row.valued ? (
              <>
                <p className="mt-1 text-2xl font-semibold tabular-nums">
                  {fmtCur(row.net_change_arr)}
                  <span className="ml-2 text-sm font-normal text-muted-foreground">
                    ({fmtCur2(row.net_change_mrr)}/mo)
                  </span>
                </p>
                <p className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {fmtCur2(row.replacement_mrr)}
                  </span>
                  <span>new</span>
                  <span>−</span>
                  <span className="font-medium text-foreground">
                    {fmtCur2(row.baseline_mrr)}
                  </span>
                  <span>
                    baseline — what the account was billed in{" "}
                    {row.prior_period ?? "the period before signing"}
                  </span>
                </p>
              </>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                No baseline can be computed. A purchase-order or reseller
                replacement sits on the <b>reseller's</b> account, so comparing it
                to account-level billing measures one sub-customer's order against
                the whole book. Review this one by hand.
              </p>
            )}
          </div>

          {/* The inputs, spelled out */}
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border px-4 py-3 sm:grid-cols-3">
            <Stat label="New MRR">
              <span className="font-semibold">{fmtCur2(row.replacement_mrr)}</span>
            </Stat>
            <Stat label="New ARR">{fmtCur(row.replacement_arr)}</Stat>
            <Stat label="Contract type">{row.contract_type_name ?? "—"}</Stat>
            <Stat label="Replaced MRR">
              {row.replaced_stack_mrr == null ? (
                <span className="text-muted-foreground">no link</span>
              ) : (
                <>
                  {fmtCur2(row.replaced_stack_mrr)}
                  {row.amendment_mrr > 0 && (
                    <span className="ml-1 text-xs text-warning">
                      (incl. {fmtCur2(row.amendment_mrr)} upsell)
                    </span>
                  )}
                </>
              )}
            </Stat>
            <Stat label="Replaced ARR">
              {row.replaced_stack_arr == null ? "—" : fmtCur(row.replaced_stack_arr)}
            </Stat>
            <Stat label={`Billed ${row.prior_period ?? "prior month"}`}>
              {row.prior_billed_mrr == null ? (
                <span
                  className="italic text-muted-foreground"
                  title="No waterfall row for that month — unknown, not zero"
                >
                  unknown
                </span>
              ) : (
                `${fmtCur2(row.prior_billed_mrr)}/mo`
              )}
            </Stat>
            <Stat label="Sign date">{fmtDate(row.sign_date)}</Stat>
            <Stat label="Billing effective">
              {row.billing_effective_from ? (
                fmtDate(row.billing_effective_from)
              ) : (
                <span className="font-medium text-warning">missing</span>
              )}
            </Stat>
            <Stat label="Closer">{row.closer_name ?? "—"}</Stat>
          </dl>

          {/* Suppression — the step's actual question */}
          <div
            className={cn(
              "rounded-md border px-4 py-3",
              FAILING.has(row.suppression) && "border-danger/40 bg-danger-light/40",
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Suppression
              </span>
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 text-sm font-medium",
                  FAILING.has(row.suppression)
                    ? "text-danger"
                    : row.suppression === "unverifiable"
                      ? "text-muted-foreground"
                      : "text-success",
                )}
              >
                {FAILING.has(row.suppression) && (
                  <TriangleAlert className="h-3.5 w-3.5" />
                )}
                {SUPPRESSION_LABELS[row.suppression]}
              </span>
            </div>

            {row.replaced_contract_id ? (
              <div className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                <p className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-foreground">
                    {row.replaced_company_name ?? "The replaced contract"}
                  </span>
                  <span>({row.replaced_type ?? "contract"})</span>
                  <span>ran from {fmtDate(row.replaced_eff_from)}</span>
                  <ArrowRight className="h-3 w-3" />
                  {row.replaced_eff_through ? (
                    <span>{fmtDate(row.replaced_eff_through)}</span>
                  ) : row.replaced_canceled_at ? (
                    <span>cancelled {fmtDate(row.replaced_canceled_at)}</span>
                  ) : (
                    <b className="text-danger">still open</b>
                  )}
                </p>
                {row.suppression === "replaced_still_open" && (
                  <p className="text-danger">
                    Both contracts are open on this account, so booked ARR counts{" "}
                    <b>{fmtCur(row.replaced_stack_arr)}</b> twice. Close the
                    superseded contract in Cinch — there's no write path from here.
                  </p>
                )}
                {row.suppression === "amendment_still_open" && (
                  <p className="text-danger">
                    {row.amendment_open_count} upsell
                    {row.amendment_open_count === 1 ? "" : "s"} amending the
                    replaced contract {row.amendment_open_count === 1 ? "is" : "are"}{" "}
                    still running past the handoff.
                  </p>
                )}
                {(row.suppression === "handoff_gap" ||
                  row.suppression === "handoff_overlap") && (
                  <p className="text-danger">
                    {row.handoff_gap_days} day
                    {Math.abs(row.handoff_gap_days ?? 0) === 1 ? "" : "s"} between
                    the old contract ending and this one starting — a clean handoff
                    is exactly 1.
                  </p>
                )}
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">
                No replaced contract is linked, so there's nothing to verify. Only
                'Replacement Contract' carries that link — every other replacement
                type needs the link backfilled in Cinch before this can be checked.
              </p>
            )}
          </div>

          {/* Why the number might not mean what it looks like */}
          {row.flags.length > 0 && (
            <div className="rounded-md border border-warning/40 bg-warning-light/40 px-4 py-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-warning">
                Read with care
              </span>
              <ul className="mt-1.5 space-y-1 text-xs text-muted-foreground">
                {row.flags.map((f) => (
                  <li key={f}>
                    • {REPLACEMENT_FLAG_LABELS[f] ?? f}
                    {f === "mixed_account_baseline" && (
                      <>
                        {" "}— {row.other_open_count} other open contract
                        {row.other_open_count === 1 ? "" : "s"} worth{" "}
                        {fmtCur2(row.other_open_mrr)}/mo, so the account total isn't
                        like-for-like with this one contract.
                      </>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <SourceLink
              href={row.pandadoc_id ? pandadocUrl(row.pandadoc_id) : null}
              label="PandaDoc"
              missing="No PandaDoc id on this contract"
            />
            <SourceLink
              href={row.hubspot_deal_id ? hubspotDealUrl(row.hubspot_deal_id) : null}
              label="HubSpot deal"
              missing="No HubSpot deal linked"
            />
          </div>

          {/* Settling this is what takes it off the backlog, for good and in
              every future month — so it sits above the per-month verdict. */}
          <ReplacementVerifyPanel period={period} row={row} canAct={canAct} />

          <ReviewVerdict
            itemKey={row.contract_id}
            status={row.review_status}
            notes={row.review_notes}
            reviewedByName={row.reviewed_by_name}
            reviewedAt={row.reviewed_at}
            canAct={canAct}
            saving={review.isPending}
            presetFlag={presetFlag}
            deniedReason="Only a RevOps or Finance owner can record a verdict."
            flagPlaceholder="What's wrong — the net, the suppression, or the record itself?"
            onSave={({ status, notes }) =>
              review.mutate(
                { contractId: row.contract_id, status, notes },
                {
                  onSuccess: (updated) =>
                    toast.success(
                      updated.review_status === "flagged"
                        ? "Replacement flagged"
                        : updated.review_status === "approved"
                          ? "Replacement approved"
                          : "Reset to pending",
                    ),
                  onError: (e) =>
                    toast.error("Couldn't save review", {
                      description: (e as Error).message,
                    }),
                },
              )
            }
          />

          {expanded && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Line by line · what changed
                </span>
                <span className="text-[11px] text-muted-foreground">
                  Contract items vs billed items
                </span>
              </div>
              {/* Where the headline net comes from, item by item — the thing that
                  turns "trust this number" into "read down the column". */}
              <ReplacementLineTable period={period} row={row} />
            </div>
          )}

          {expanded && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Revenue impact · 12 months before signing
                </span>
                <span className="text-[11px] text-muted-foreground">
                  Billed actuals from the waterfall
                </span>
              </div>
              {history.isLoading && (
                <div className="h-48 animate-pulse rounded-md border bg-muted/30" />
              )}
              {history.error && (
                <p className="rounded-md border border-dashed px-3 py-4 text-xs text-muted-foreground">
                  Couldn't load the billing history: {(history.error as Error).message}
                </p>
              )}
              {history.data && <RevenueImpact h={history.data} />}
            </div>
          )}
        </div>
      )}
    </EntityDrawer>
  );
}

/** The two readings, side by side.
 *
 *  Subscription-only answers "how does the new contract compare to what they
 *  were paying" — the contracted commitment moving. All-in adds usage on top at
 *  its recent run rate, because a replacement doesn't stop usage; on a
 *  usage-heavy account that's the larger half of the relationship.
 *
 *  They can point different ways, which is the reason both are here. */
function RevenueImpact({ h }: { h: ReplacementHistory }) {
  if (h.months_observed === 0) {
    return (
      <p className="rounded-md border border-dashed px-3 py-4 text-xs text-muted-foreground">
        No billing history for this account in the twelve months before signing —
        there's nothing to compare the new contract against.
      </p>
    );
  }
  const peak = Math.max(...h.months.map((m) => m.total_billed), 1);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Comparison
          title="Subscription only"
          caption="The contracted commitment, before and after."
          was={h.actual_subscription}
          wasLabel="Billed, trailing 12"
          will={h.new_subscription}
          willLabel="New contract × 12"
          change={h.subscription_change}
        />
        <Comparison
          title="All-in, usage continuing"
          caption={`Usage carried on top at its last ${h.usage_projection_months}-month average (${fmtCur2(h.projected_usage_monthly)}/mo).`}
          was={h.actual_all_in}
          wasLabel="Billed, trailing 12"
          will={h.projected_all_in}
          willLabel="New contract + usage"
          change={h.all_in_change}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        Usage is not part of the contract, so a replacement doesn't stop it — the
        all-in view assumes it continues. Where a replacement instead <i>folds</i>{" "}
        former usage into its subscription, the all-in view double-counts; say so
        in the notes.
      </p>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead align="right">Subscription</TableHead>
              <TableHead align="right">Usage / other</TableHead>
              <TableHead align="right">Total billed</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {h.months.map((m) => (
              <TableRow key={m.period}>
                <TableCell className="whitespace-nowrap text-xs">{m.period}</TableCell>
                <TableCell numeric className="text-xs">
                  {fmtCur2(m.subscription)}
                </TableCell>
                <TableCell numeric className="text-xs">
                  {fmtCur2(m.usage_other)}
                </TableCell>
                <TableCell numeric className="text-xs font-medium">
                  {fmtCur2(m.total_billed)}
                </TableCell>
                <TableCell>
                  {/* Subscription vs usage at a glance — the split is the point
                      on accounts where usage outweighs the contract. */}
                  <span className="flex h-2 w-20 overflow-hidden rounded-full bg-muted">
                    <span
                      className="bg-brand"
                      style={{ width: `${(m.subscription / peak) * 100}%` }}
                    />
                    <span
                      className="bg-warning"
                      style={{ width: `${(m.usage_other / peak) * 100}%` }}
                    />
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Comparison({
  title,
  caption,
  was,
  wasLabel,
  will,
  willLabel,
  change,
}: {
  title: string;
  caption: string;
  was: number;
  wasLabel: string;
  will: number;
  willLabel: string;
  change: number;
}) {
  return (
    <div className="rounded-md border px-4 py-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold">{title}</span>
        <DeltaCell value={change} chip emptyLabel="no change" />
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{caption}</p>
      <dl className="mt-2 space-y-1 text-xs">
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{wasLabel}</dt>
          <dd className="tabular-nums">{fmtCur(was)}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-muted-foreground">{willLabel}</dt>
          <dd className="font-semibold tabular-nums">{fmtCur(will)}</dd>
        </div>
      </dl>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{children}</dd>
    </div>
  );
}

function SourceLink({
  href,
  label,
  missing,
}: {
  href: string | null;
  label: string;
  missing: string;
}) {
  if (!href) {
    return (
      <span className="rounded-md border border-dashed px-2.5 py-1 text-xs text-muted-foreground">
        {missing}
      </span>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="rounded-md border px-2.5 py-1 text-xs font-medium text-brand hover:bg-muted"
    >
      {label} ↗
    </a>
  );
}
